import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from '@zafroi/ui-kit';
import {
  AlertTriangle,
  CheckCircle2,
  Inbox,
  Link2Off,
  LogIn,
  Loader2,
  Mail,
  Palette,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import {
  createChannel,
  deleteChannel,
  disconnectChannel,
  startGmailOAuth,
  testChannelConnection,
  updateChannel,
} from '@/services/engage-api';
import { ApiError } from '@/services/api-client';
import { useTenant } from '@/services/tenant-context';
import { relativeTime } from '@/lib/format';
import type { Channel, GmailMessageSummary } from '@/types/engage';

/**
 * EmailChannelSheet — the configure flow for the EMAIL channel.
 *
 * Sections:
 *  1. Identity     — the support email address (`identifier`).
 *  2. Auto-respond — tenant-defined subject + body templates with clickable
 *                    placeholders and a live preview.
 *  3. Status       — smart panel that varies by channel.status:
 *                    - no channel yet: info-only ("save first")
 *                    - disconnected:   "Connect Gmail" + pitch
 *                    - pending:        "Connecting…" spinner
 *                    - connected:      pill + "Test connection" + "Disconnect"
 *                                       + optional inline result panel
 *                    - error:          red banner + "Reconnect Gmail"
 *
 * Uses `Sheet` rather than a centered Dialog because the kit does not export
 * a Dialog primitive yet and Sheet already gives us focus-trap, scroll-lock
 * and Escape-to-close — matching `UpgradeModal` and `AssistantPanel`.
 */

export interface EmailChannelSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Editing an existing channel — when null, the sheet creates a new one. */
  channel: Channel | null;
}

export function EmailChannelSheet({
  open,
  onOpenChange,
  channel,
}: EmailChannelSheetProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const isEditing = channel !== null;

  const [identifier, setIdentifier] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  // Set when the user clicks "Test connection" and the runtime returns the
  // last-N inbox messages. Local-only state — never refetched on its own.
  const [testMessages, setTestMessages] = useState<GmailMessageSummary[] | null>(
    null,
  );

  // Initialise / reset the form whenever the sheet re-opens or the underlying
  // channel changes. Templates + brand are now edited on the dedicated
  // full-page editor (see the "Email templates" card below).
  useEffect(() => {
    if (!open) return;
    setIdentifier(channel?.identifier ?? '');
    setConfirmingDelete(false);
    setConfirmingDisconnect(false);
    setTestMessages(null);
  }, [open, channel]);

  const queryKey = useMemo(
    () => ['channels', currentTenant.id] as const,
    [currentTenant.id],
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { identifier: identifier.trim() || undefined };
      if (isEditing) {
        return updateChannel(channel!.id, payload);
      }
      // Templates seed to sensible branded defaults on the backend; the tenant
      // refines them on the dedicated editor after the channel exists.
      return createChannel({
        kind: 'email',
        label: 'Support inbox',
        ...payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(
        isEditing
          ? 'Email channel updated'
          : 'Email channel created — connect a Gmail account next.',
      );
      onOpenChange(false);
    },
    onError: () =>
      toast.error("Couldn't save the email channel — try again."),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteChannel(channel!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Email channel removed.');
      onOpenChange(false);
    },
    onError: () =>
      toast.error("Couldn't remove the channel — try again."),
  });

  // ── OAuth / connection mutations ────────────────────────────────────────
  // Connect: save first (in case the editor has unsaved edits) so the channel
  // record is fresh when the runtime mints the auth URL, then ask the runtime
  // for the URL and full-page-redirect there. Google does not allow popup or
  // iframe flows for this scope set — it must own the whole tab.
  const connectMutation = useMutation({
    mutationFn: async () => {
      if (!channel) throw new Error('No channel to connect.');
      // Persist any unsaved identifier edit first — fire-and-await so the
      // runtime has the latest identifier when it mints the OAuth state.
      await updateChannel(channel.id, {
        identifier: identifier.trim() || undefined,
      });
      return startGmailOAuth(channel.id);
    },
    onSuccess: ({ authUrl }) => {
      // Full-page redirect — OAuth requires it; popups are blocked by Google.
      window.location.href = authUrl;
    },
    onError: (err: unknown) => {
      const raw =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Could not start Gmail connection.';
      if (raw.includes('Google OAuth is not configured')) {
        toast.error('Gmail connection is not yet enabled', {
          description:
            'The operator needs to complete the GCP setup. See CHANNELS.md.',
        });
      } else {
        toast.error("Couldn't start Gmail connection", {
          description: raw,
        });
      }
    },
  });

  const testMutation = useMutation({
    mutationFn: () => testChannelConnection(channel!.id),
    onSuccess: (messages) => {
      setTestMessages(messages);
      toast.success(`Fetched ${messages.length} inbox message${messages.length === 1 ? '' : 's'}.`);
    },
    onError: (err: unknown) => {
      const raw =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Could not reach Gmail.';
      toast.error("Connection test failed", { description: raw });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectChannel(channel!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setConfirmingDisconnect(false);
      setTestMessages(null);
      toast.success('Gmail disconnected. Your template is preserved.');
    },
    onError: () =>
      toast.error("Couldn't disconnect Gmail — try again."),
  });

  const isBusy =
    saveMutation.isPending ||
    deleteMutation.isPending ||
    connectMutation.isPending ||
    testMutation.isPending ||
    disconnectMutation.isPending;
  // Identity is optional (Gmail OAuth fills it from the connected profile),
  // so saving is always allowed — the channel just needs to exist.
  const canSave = true;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-2xl sm:max-w-3xl overflow-y-auto"
      >
        <SheetHeader className="pr-12">
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            {isEditing ? 'Configure email channel' : 'Add email channel'}
          </SheetTitle>
          <SheetDescription>
            Receive customer emails into Engage, auto-acknowledge them, and turn
            each into a tracked ticket.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-5 py-5">
          {/* ── Step 1: Identity ──────────────────────────────────── */}
          <section className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <h4 className="text-sm font-semibold text-foreground">
                1. Identity
              </h4>
              <Badge tone="neutral" withDot={false}>
                Required
              </Badge>
            </div>
            <Label htmlFor="ch-email-identifier" className="text-xs">
              Customer support email
            </Label>
            <Input
              id="ch-email-identifier"
              type="email"
              placeholder="support@yourcompany.com"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={isBusy}
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              The email address customers will write to. Must be an address you
              own — once saved, use the Connect Gmail button below to authorise
              Engage to read and reply on its behalf.
            </p>
          </section>

          {/* ── Step 2: Branded email templates ───────────────────── */}
          <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-2">
              <h4 className="text-sm font-semibold text-foreground">
                2. Email templates
              </h4>
              <Badge tone="info" withDot={false}>
                Branded
              </Badge>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Design the branded emails customers receive — the
              auto-acknowledgement, your agents' replies, and the resolved
              notification — with your logo, colours, and a live preview.
            </p>
            {isEditing ? (
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/channels/email/${channel!.id}/templates`);
                }}
              >
                <Palette className="h-4 w-4" />
                Open email template editor
              </Button>
            ) : (
              <div className="rounded-md border border-dashed border-border bg-muted/40 p-3 text-[11px] leading-relaxed text-muted-foreground">
                Save this channel first — then a dedicated editor opens here to
                design your branded templates. Sensible defaults are applied
                until you customise them.
              </div>
            )}
          </section>

          {/* ── Step 3: Status ────────────────────────────────────── */}
          <section className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <h4 className="text-sm font-semibold text-foreground">
                3. Connection status
              </h4>
              {channel && (
                <Badge
                  tone={
                    channel.status === 'connected'
                      ? 'success'
                      : channel.status === 'error'
                        ? 'destructive'
                        : channel.status === 'pending'
                          ? 'warning'
                          : 'neutral'
                  }
                  withDot
                >
                  {channel.status === 'connected'
                    ? 'Connected'
                    : channel.status === 'error'
                      ? 'Error'
                      : channel.status === 'pending'
                        ? 'Connecting'
                        : 'Not connected'}
                </Badge>
              )}
            </div>

            <ConnectionStatusPanel
              channel={channel}
              isBusy={isBusy}
              connecting={connectMutation.isPending}
              testing={testMutation.isPending}
              disconnecting={disconnectMutation.isPending}
              confirmingDisconnect={confirmingDisconnect}
              setConfirmingDisconnect={setConfirmingDisconnect}
              testMessages={testMessages}
              onConnect={() => connectMutation.mutate()}
              onTest={() => testMutation.mutate()}
              onDisconnect={() => disconnectMutation.mutate()}
              onHideTest={() => setTestMessages(null)}
            />
          </section>
        </div>

        {/* Footer actions */}
        <div className="flex flex-col gap-2 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {isEditing &&
              (confirmingDelete ? (
                <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-[11px] text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>Delete this channel?</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => setConfirmingDelete(false)}
                  >
                    No
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => deleteMutation.mutate()}
                  >
                    {deleteMutation.isPending ? 'Removing…' : 'Yes, delete'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => setConfirmingDelete(true)}
                  className={cn('text-destructive hover:text-destructive')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete channel
                </Button>
              ))}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              variant="ghost"
              size="sm"
              disabled={isBusy}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canSave || isBusy}
              onClick={() => saveMutation.mutate()}
            >
              <Save className="h-3.5 w-3.5" />
              {saveMutation.isPending
                ? 'Saving…'
                : isEditing
                  ? 'Save changes'
                  : 'Create channel'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Connection status panel ──────────────────────────────────────────────
// Renders the right affordances for the channel's current connection state.
// Lives inside the same file because it shares the OAuth/test/disconnect
// mutations + local state with the parent sheet — no point pulling it out.

interface ConnectionStatusPanelProps {
  channel: Channel | null;
  isBusy: boolean;
  connecting: boolean;
  testing: boolean;
  disconnecting: boolean;
  confirmingDisconnect: boolean;
  setConfirmingDisconnect: (next: boolean) => void;
  testMessages: GmailMessageSummary[] | null;
  onConnect: () => void;
  onTest: () => void;
  onDisconnect: () => void;
  onHideTest: () => void;
}

function ConnectionStatusPanel({
  channel,
  isBusy,
  connecting,
  testing,
  disconnecting,
  confirmingDisconnect,
  setConfirmingDisconnect,
  testMessages,
  onConnect,
  onTest,
  onDisconnect,
  onHideTest,
}: ConnectionStatusPanelProps) {
  // ── Pre-save (channel is null): info-only ───────────────────────────────
  if (!channel) {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
        Save the channel first — then you'll be able to connect this channel
        to your Gmail account.
      </div>
    );
  }

  // ── Pending: transient OAuth round-trip state ───────────────────────────
  if (channel.status === 'pending') {
    return (
      <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2.5 text-xs text-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-warning" />
        Connecting to Gmail… this usually only takes a few seconds.
      </div>
    );
  }

  // ── Error: red banner + reconnect ───────────────────────────────────────
  if (channel.status === 'error') {
    return (
      <div className="space-y-2">
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs leading-relaxed text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">
              We hit a problem with your Gmail connection.
            </p>
            <p className="mt-0.5 text-destructive/90">
              Reconnect to grant fresh access — your template and inbox address
              are preserved.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={isBusy}
          onClick={onConnect}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {connecting ? 'Starting…' : 'Reconnect Gmail'}
        </Button>
      </div>
    );
  }

  // ── Connected: pill + test + disconnect + (optional) test results ──────
  if (channel.status === 'connected') {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-success/40 bg-success/5 p-3 text-xs">
          <CheckCircle2 className="h-3.5 w-3.5 text-success" />
          <span className="font-medium text-foreground">
            Connected as{' '}
            <span className="font-mono">
              {channel.identifier ?? 'your Gmail account'}
            </span>
          </span>
          {channel.lastSyncedAt && (
            <span className="text-muted-foreground">
              · last synced {relativeTime(channel.lastSyncedAt)}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={isBusy}
            onClick={onTest}
          >
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                testing && 'animate-spin',
              )}
            />
            {testing ? 'Testing…' : 'Test connection'}
          </Button>

          {confirmingDisconnect ? (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-[11px] text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Disconnect Gmail?</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={isBusy}
                onClick={() => setConfirmingDisconnect(false)}
              >
                No
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={isBusy}
                onClick={onDisconnect}
              >
                {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={isBusy}
              onClick={() => setConfirmingDisconnect(true)}
              className={cn('text-destructive hover:text-destructive')}
            >
              <Link2Off className="h-3.5 w-3.5" />
              Disconnect
            </Button>
          )}
        </div>

        {testMessages && (
          <div className="rounded-lg border border-border bg-card p-3 shadow-elev-1">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Inbox className="h-3 w-3" />
                Latest {testMessages.length} inbox message
                {testMessages.length === 1 ? '' : 's'}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={onHideTest}
                className="h-6 px-2 text-[11px]"
              >
                <X className="h-3 w-3" />
                Hide
              </Button>
            </div>
            {testMessages.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">
                Inbox is empty — but the connection is working.
              </p>
            ) : (
              <ul className="space-y-2">
                {testMessages.map((m) => (
                  <li
                    key={m.id}
                    className="rounded border border-border bg-background/60 p-2.5 text-xs"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate font-medium text-foreground">
                        {m.from}
                      </span>
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {relativeTime(m.date)}
                      </span>
                    </div>
                    <div className="truncate text-foreground">{m.subject}</div>
                    <div className="mt-0.5 line-clamp-2 text-muted-foreground">
                      {m.snippet}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Disconnected (default): pitch + Connect Gmail button ────────────────
  return (
    <div className="space-y-2">
      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
        We'll redirect you to Google to authorise read + send access to your
        inbox. We never see your password — Google issues a token that we
        store securely on your behalf.
      </div>
      <Button
        size="sm"
        disabled={isBusy}
        onClick={onConnect}
      >
        <LogIn className="h-3.5 w-3.5" />
        {connecting ? 'Starting…' : 'Connect Gmail'}
      </Button>
    </div>
  );
}
